import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SpinnerService } from '../../../core/shared/services/spinner.service';
import { extractErrorMessage } from '../../../core/shared/util/error.util';
import { nomesTitulares } from '../models/siump-analise.helpers';
import { SiumpAnalise, SiumpFiltroSituacao, SiumpOperacao } from '../models/siump-analise.model';
import { AnalisarArquivoSiumpUseCase } from '../usecases/analisar-arquivo-siump.usecase';

interface FgtsSiumpState {
  analise: SiumpAnalise | null;
  loading: boolean;
  error: string | null;
  situacao: SiumpFiltroSituacao;
  busca: string;
  modoTecnico: boolean;
}

const ESTADO_INICIAL: FgtsSiumpState = {
  analise: null,
  loading: false,
  error: null,
  situacao: 'TODAS',
  busca: '',
  modoTecnico: false
};

@Injectable({ providedIn: 'root' })
export class FgtsSiumpAnaliseViewModel {
  private readonly useCase = inject(AnalisarArquivoSiumpUseCase);
  private readonly spinner = inject(SpinnerService);

  private readonly _state = signal<FgtsSiumpState>(ESTADO_INICIAL);

  readonly state = this._state.asReadonly();
  readonly analise = computed(() => this._state().analise);
  readonly loading = computed(() => this._state().loading);
  readonly error = computed(() => this._state().error);
  readonly situacao = computed(() => this._state().situacao);
  readonly busca = computed(() => this._state().busca);
  readonly modoTecnico = computed(() => this._state().modoTecnico);

  readonly operacoesFiltradas = computed<SiumpOperacao[]>(() => {
    const { analise, situacao, busca } = this._state();
    if (!analise) {
      return [];
    }
    const termo = busca.trim().toLowerCase();
    return analise.operations.filter(operacao => {
      if (situacao !== 'TODAS' && operacao.status !== situacao) {
        return false;
      }
      if (!termo) {
        return true;
      }
      return (
        operacao.operationId.toLowerCase().includes(termo) ||
        nomesTitulares(operacao).some(nome => nome.toLowerCase().includes(termo))
      );
    });
  });

  readonly contagemPorSituacao = computed(() => {
    const operacoes = this._state().analise?.operations ?? [];
    return {
      TODAS: operacoes.length,
      ACATADA: operacoes.filter(o => o.status === 'ACATADA').length,
      REJEITADA: operacoes.filter(o => o.status === 'REJEITADA').length,
      PENDENTE: operacoes.filter(o => o.status === 'PENDENTE').length,
      NAO_PROCESSADA: operacoes.filter(o => o.status === 'NAO_PROCESSADA').length
    } satisfies Record<SiumpFiltroSituacao, number>;
  });

  readonly temErros = computed(() => (this._state().analise?.summary.diagnosticCounts.ERRO ?? 0) > 0);

  async analisar(arquivo: File): Promise<boolean> {
    this._state.set({ ...ESTADO_INICIAL, loading: true });
    this.spinner.show();
    try {
      const analise = await firstValueFrom(this.useCase.execute(arquivo));
      this._state.update(state => ({ ...state, analise, loading: false }));
      return true;
    } catch (error) {
      this._state.update(state => ({
        ...state,
        loading: false,
        error: extractErrorMessage(error, 'Não foi possível analisar o arquivo SIUMP.')
      }));
      return false;
    } finally {
      this.spinner.hide();
    }
  }

  limpar(): void {
    this._state.set(ESTADO_INICIAL);
  }

  definirSituacao(situacao: SiumpFiltroSituacao): void {
    this._state.update(state => ({ ...state, situacao }));
  }

  definirBusca(busca: string): void {
    this._state.update(state => ({ ...state, busca }));
  }

  alternarModoTecnico(): void {
    this._state.update(state => ({ ...state, modoTecnico: !state.modoTecnico }));
  }
}
